import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogTrigger } from 'shared/components/ui/dialog';
import { AmountInput } from 'shared/components/ui/AmountInput';
import { IconNote } from 'shared/components/ui/IconNote';
import colorsStore from 'shared/store/colorsStore';
import { AssistantService } from 'shared/services/AssistantService';
import { ProductCostData, PortfolioProduct, SimulationInputs, AggregatedCostData } from './types';
import { CostiTable } from './CostiTable';
import {
  aggregateCostData,
  calculateSimulationResults,
  extractProductsFromPortfolioData,
  findProductsWithMissingCostData,
  findPoliciesWithoutCostData,
} from './calculations';
import styles from './simulazioneCosti.module.scss';
import { createLogger } from 'shared/logger';

const log = createLogger('SimulazioneCosti');

interface SimulazioneCostiModalProps {
  portfolioData: any;
  portfolioId: string;
  children: React.ReactNode;
}

export const SimulazioneCostiModal: React.FC<SimulazioneCostiModalProps> = ({
  portfolioData,
  portfolioId,
  children,
}) => {
  useTranslation();
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [costData, setCostData] = useState<ProductCostData[]>([]);

  // Extract products from portfolio data
  const portfolioProducts = useMemo<PortfolioProduct[]>(() => {
    return extractProductsFromPortfolioData(portfolioData);
  }, [portfolioData]);

  // Calculate total portfolio value from asset values
  const totalPortfolioValue = useMemo(() => {
    // Try different paths for assets array
    const assets = portfolioData?.data?.data?.assets || portfolioData?.data?.assets || portfolioData?.assets;

    if (Array.isArray(assets)) {
      const total = assets.reduce((sum, asset) => {
        const value = asset?.value;
        return sum + (value != null ? parseFloat(value) : 0);
      }, 0);
      return Math.round(total * 100) / 100;
    }
    return 0;
  }, [portfolioData]);

  // Simulation inputs state
  const [inputs, setInputs] = useState<SimulationInputs>({
    portfolioValue: 0,
    feeRegime: 'fee_only',
    feeOnly: {
      commissioneConsulenza: 0,
      serviziAggiuntivi: 0,
    },
    feeOnTop: {
      commissioneConsulenza: 0,
      serviziAggiuntivi: 0,
      feeFondi: null,
      feeETF: null,
      feeAmministrato: null,
      includiFondiFAM: true,
    },
  });

  // Update portfolio value when data changes
  useEffect(() => {
    if (totalPortfolioValue > 0) {
      setInputs((prev) => ({
        ...prev,
        portfolioValue: totalPortfolioValue,
      }));
    }
  }, [totalPortfolioValue]);

  // Fetch cost data when modal opens
  useEffect(() => {
    if (open && portfolioProducts.length > 0 && costData.length === 0) {
      const fetchCostData = async () => {
        setLoading(true);
        try {
          const isins = portfolioProducts.map((p) => p.isin);
          const response = await AssistantService.fetchProductsCostData(isins);
          setCostData(response?.products || []);
        } catch (error) {
          log.error('fetch cost data failed', { error });
        } finally {
          setLoading(false);
        }
      };
      fetchCostData();
    }
  }, [open, portfolioProducts, costData.length]);

  // Aggregate cost data at portfolio level (only when dialog is open and data is loaded)
  const aggregatedData = useMemo<AggregatedCostData>(() => {
    if (!open || costData.length === 0) {
      return {
        commissioniGestione: 0,
        altreCommissioniRicorrenti: 0,
        commissioniTransazione: 0,
        commissioniPerformance: 0,
        abbuoniPercentage: 0,
      };
    }
    return aggregateCostData(portfolioProducts, costData);
  }, [open, portfolioProducts, costData]);

  // Find products with missing cost data (null values)
  const productsWithMissingCostData = useMemo<string[]>(() => {
    if (!open || costData.length === 0) {
      return [];
    }
    return findProductsWithMissingCostData(costData);
  }, [open, costData]);

  const policiesWithoutCostData = useMemo<string[]>(
    () => (open ? findPoliciesWithoutCostData(costData) : []),
    [open, costData],
  );

  // Calculate simulation results (only when dialog is open and data is loaded)
  const results = useMemo(() => {
    if (!open || costData.length === 0) {
      return {
        commissioneConsulenzaEuro: 0,
        serviziAggiuntiviEuro: 0,
        feeFondiEuro: 0,
        feeETFEuro: 0,
        feeAmministratoEuro: 0,
        abbuoniEuro: 0,
        commissioniGestioneEuro: 0,
        altreCommissioniRicorrentiEuro: 0,
        commissioniTransazioneEuro: 0,
        commissioniPerformanceEuro: 0,
        totaleCostiEuro: 0,
        totaleCostiPercentage: 0,
        addebitiNettiCCEuro: 0,
        addebitiNettiCCPercentage: 0,
        stimaRebatesPfaEuro: 0,
        stimaRebatesPfaPercentage: 0,
      };
    }
    return calculateSimulationResults(inputs, aggregatedData, portfolioProducts, costData);
  }, [open, inputs, aggregatedData, portfolioProducts, costData]);

  const handleInputChange = (field: string, value: any) => {
    setInputs((prev) => {
      // Handle top-level fields
      if (field === 'portfolioValue' || field === 'feeRegime') {
        return { ...prev, [field]: value };
      }

      // Handle Fee Only fields
      if (field === 'feeOnly.commissioneConsulenza' || field === 'feeOnly.serviziAggiuntivi') {
        const feeOnlyField = field.replace('feeOnly.', '') as keyof typeof prev.feeOnly;
        return {
          ...prev,
          feeOnly: { ...prev.feeOnly, [feeOnlyField]: value },
        };
      }

      // Handle Fee on Top fields
      if (field.startsWith('feeOnTop.')) {
        const feeOnTopField = field.replace('feeOnTop.', '') as keyof typeof prev.feeOnTop;
        const newFeeOnTop = { ...prev.feeOnTop, [feeOnTopField]: value };

        // Handle Unica vs Differenziata alternative input logic
        if (feeOnTopField === 'commissioneConsulenza' && value > 0) {
          // If setting commissioneConsulenza, clear differenziata fields
          newFeeOnTop.feeFondi = null;
          newFeeOnTop.feeETF = null;
          newFeeOnTop.feeAmministrato = null;
        } else if (
          (feeOnTopField === 'feeFondi' || feeOnTopField === 'feeETF' || feeOnTopField === 'feeAmministrato') &&
          value !== null &&
          value > 0
        ) {
          // If setting any differenziata field, clear commissioneConsulenza
          newFeeOnTop.commissioneConsulenza = 0;
        }

        return {
          ...prev,
          feeOnTop: newFeeOnTop,
        };
      }

      return prev;
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent
        title="Analisi Costi"
        className="max-w-[800px]"
        style={{
          backgroundColor: 'white',
          color: colorsConfig.finecoBlack,
        }}
      >
        {loading ? (
          <div className={styles.loadingContainer}>
            <div className={styles.spinner} />
            <span>Caricamento dati...</span>
          </div>
        ) : (
          <div className={styles.modalContent}>
            {/* Scrollable body */}
            <div className={styles.modalBody}>
              {/* Portfolio Value Input */}
              <div className={styles.portfolioValueSection}>
                <label className={styles.portfolioValueLabel}>Controvalore di portafoglio</label>
                <AmountInput
                  value={inputs.portfolioValue || ''}
                  onChangeValue={(v) => handleInputChange('portfolioValue', parseFloat(v) || 0)}
                  className={styles.portfolioValueInput}
                  placeholder="Inserisci valore (€)"
                />
              </div>

              {/* Fee Regime Toggle */}
              <div className={styles.feeRegimeToggle}>
                <button
                  onClick={() => handleInputChange('feeRegime', 'fee_only')}
                  className={`${styles.feeRegimeButton} ${
                    inputs.feeRegime === 'fee_only' ? styles.active : styles.inactive
                  }`}
                >
                  Fee only
                </button>
                <button
                  onClick={() => handleInputChange('feeRegime', 'fee_on_top')}
                  className={`${styles.feeRegimeButton} ${
                    inputs.feeRegime === 'fee_on_top' ? styles.active : styles.inactive
                  }`}
                >
                  Fee on top
                </button>
              </div>

              {/* Cost Table */}
              <CostiTable
                inputs={inputs}
                aggregatedData={aggregatedData}
                results={results}
                onInputChange={handleInputChange}
              />

              {/* Warning for products with missing cost data */}
              {productsWithMissingCostData.length > 0 && (
                <IconNote
                  type="attention"
                  title="Attenzione"
                  body={
                    productsWithMissingCostData.length === 1
                      ? `Per il prodotto ${productsWithMissingCostData[0]} non è disponibile il dato dei costi, che è stato fissato a 0%, pertanto i valori sopra riportati sono impattati da tale approssimazione.`
                      : `Per i prodotti ${productsWithMissingCostData.join(', ')} non sono disponibili i dati dei costi, che sono stati fissati a 0%, pertanto i valori sopra riportati sono impattati da tale approssimazione.`
                  }
                  className={styles.missingCostWarning}
                />
              )}

              {policiesWithoutCostData.length > 0 && (
                <IconNote
                  type="note"
                  title="Polizze"
                  body={`Costi n.d. per ${policiesWithoutCostData.join(', ')}: i costi delle polizze non sono disponibili e non sono inclusi nei valori sopra riportati.`}
                  className={styles.missingCostWarning}
                />
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
